/**
 * Permission catalogue.
 *
 * Codes are the reference product's permission codes, kept so that roles map one to one.
 * The reference has 738 codes (walk of 2026-10-27); all are here. Those no 212Ez screen uses yet
 * come from `permissionsReference.ts`.
 * Descriptions are written for this project. `implemented` marks codes the API enforces today.
 */

import { REFERENCE_ONLY_PERMISSIONS } from './permissionsReference';

export interface PermissionDef {
  code: string;
  name: string;
  category: string;
  description: string;
  implemented?: boolean;
  /** Listed only because the reference's checklist has it (switch `permissions-all`). */
  referenceOnly?: boolean;
}

const p = (
  code: number,
  name: string,
  category: string,
  description: string,
  implemented = false,
): PermissionDef => ({ code: `PC${code}`, name, category, description, implemented });

export const PERMISSIONS: PermissionDef[] = [
  // Dashboard
  p(166, 'Dashboard Quick Stats', 'Dashboard', 'Load the quick statistics panel.', true),
  p(170, 'Subscribers by State', 'Dashboard', 'View the subscribers-by-state chart.', true),
  p(
    171,
    'Activation/Deactivation',
    'Dashboard',
    'View monthly activation and deactivation counts.',
    true,
  ),
  p(
    174,
    'Inactive Subscribers by Reason',
    'Dashboard',
    'View inactive subscribers grouped by reason.',
    true,
  ),
  p(
    175,
    'Subscribers by Master Agent',
    'Dashboard',
    'View subscriber totals per master agent.',
    true,
  ),
  p(1154, 'Sales Data', 'Dashboard', 'View sales totals by the user who took the payment.', true),
  p(1158, 'Agents Data', 'Dashboard', 'View agent sales totals.', true),

  // Customer Service
  p(400, 'Customer Service', 'Customer Service', 'Open the Customer Service menu.', true),
  p(
    56,
    'Customer Profile',
    'Customer Service',
    'Search customers and open a customer profile.',
    true,
  ),
  p(75, 'Billing and Invoice', 'Customer Service', 'View a customer’s bills and invoices.', true),
  p(76, 'Order History', 'Customer Service', 'View a customer’s orders.', true),
  p(58, 'Purchase History', 'Customer Service', 'View a customer’s purchases.', true),
  p(87, 'Customer Tickets', 'Customer Service', 'View the tickets of a customer.', true),
  p(770, 'Customer Activity Log', 'Customer Service', 'View the activity log of a customer.', true),
  p(900, 'Manage Saved Card', 'Customer Service', 'Manage a customer’s saved payment cards.', true),
  p(930, 'Resend Notification', 'Customer Service', 'Resend a notification to a customer.', true),
  p(295, 'Recent Searches', 'Customer Service', 'View your own recent customer searches.', true),
  p(
    339,
    'Payment Search Tool',
    'Customer Service',
    'Search payments by amount, confirmation or card.',
    true,
  ),
  p(544, 'Dealer Wallet', 'Customer Service', 'View and fund the dealer wallet.', true),
  p(604, 'Remove Wallet Funds', 'Customer Service', 'Remove funds from a dealer wallet.', true),

  // Customer profile actions (milestone M4)
  p(401, 'Modify', 'Customer Service', 'Open the Modify page of a customer.', true),
  p(
    787,
    'Customer Classification',
    'Customer Service',
    'Change the classification of a customer (Update Customer Classification).',
    true,
  ),
  p(
    377,
    'Customer Information',
    'Customer Service',
    'Edit a customer’s name, language and contact details.',
    true,
  ),
  p(
    100,
    'Change Address',
    'Customer Service',
    'Change a customer’s service or mailing address.',
    true,
  ),
  p(18, 'ESN/SIM Change', 'Customer Service', 'Replace the SIM on a customer’s line.', true),
  p(83, 'Add Notes', 'Customer Service', 'Add a note to a customer.', true),
  p(
    402,
    'Create Ticket From Note',
    'Customer Service',
    'The "Do you want to create a ticket?" link of Add New Note.',
    true,
  ),
  p(
    543,
    'Add Line',
    'Customer Service',
    'Start an enrollment for another line from a customer’s page.',
    true,
  ),
  p(
    459,
    'Tax Exemption Setting',
    'Customer Service',
    'Exempt a customer from tax on kinds of charge.',
    true,
  ),
  p(
    932,
    'Declined Payment',
    'Customer Service',
    'See the payments that were tried and refused.',
    true,
  ),
  p(
    465,
    'OCS Purchase',
    'Customer Service',
    'See the purchases the carrier holds for the line.',
    true,
  ),
  p(678, 'Autopay Log', 'Customer Service', 'See what Auto Pay did for a customer.', true),
  p(
    810,
    'Disable Credit Card',
    'Customer Service',
    'Switch "Disable Credit Card" on a customer’s page.',
    true,
  ),
  p(
    846,
    'Do not sell info',
    'Customer Service',
    'Switch "Do not sell info" on a customer’s page.',
    true,
  ),
  p(847, 'Delete Data', 'Customer Service', 'Switch "Delete Data" on a customer’s page.', true),
  p(
    648,
    'Change Account Type',
    'Customer Service',
    'Move an Active prepaid customer into ACP or Lifeline (CSR Screen For Change Account Type).',
    true,
  ),
  p(
    945,
    'Add Product',
    'Customer Service',
    'Send the product of the line’s plan to the carrier (CSR Screen For Add Product).',
    true,
  ),
  p(
    248,
    'Mark Void',
    'Customer Service',
    'Void (archive) a customer note; it moves to View Archive Notes.',
    true,
  ),
  p(
    805,
    'Notification Preference',
    'Customer Service',
    'Change a customer’s contact preferences.',
    true,
  ),
  p(95, 'Change Account Status', 'Customer Service', 'Open Update Account Status.', true),
  p(500, 'Dispatched to Active', 'Customer Service', 'Activate a dispatched customer.', true),
  p(636, 'Suspend Customer', 'Customer Service', 'Suspend an active customer.', true),
  p(637, 'Inactive Customer', 'Customer Service', 'Disconnect a customer.', true),
  p(638, 'Restore Customer', 'Customer Service', 'Restore a suspended customer.', true),
  p(639, 'Reconnect Customer', 'Customer Service', 'Reconnect an inactive customer.', true),

  // Enrollment handover and payment (milestone M4)
  p(251, 'Hand to Hand Handover', 'Orders', 'Hand a SIM to the customer during enrollment.', true),
  p(
    250,
    'BYOD Handover',
    'Orders',
    'Enroll a customer on their own device (BYOD- Sprint/Verzion).',
    true,
  ),
  p(318, 'Cash Payment Handover', 'Orders', 'Take cash at the end of an enrollment.', true),
  p(320, 'Skip Payment Handover', 'Orders', 'Complete an enrollment without taking payment.', true),

  // Ports
  p(393, 'Ports', 'Ports', 'Open the Ports menu.', true),
  p(799, 'Port In', 'Ports', 'Open the port-in submenu.', true),
  p(
    394,
    'Pending Portin / Manual Queue',
    'Ports',
    'Work the pending and manual port-in queues.',
    true,
  ),
  p(395, 'Failed / Cancelled Portin', 'Ports', 'View failed and cancelled port-ins.', true),
  p(396, 'Completed Portin', 'Ports', 'View completed port-ins.', true),
  p(
    343,
    'Existing Subscriber Portin',
    'Ports',
    'Port a number in for an existing subscriber.',
    true,
  ),
  p(778, 'Portin History', 'Ports', 'View the port-in request log.', true),
  p(800, 'Port Out', 'Ports', 'Open the port-out submenu.', true),
  p(815, 'T-Mobile Completed Portout', 'Ports', 'View completed port-outs for this carrier.', true),
  p(923, 'Plintron Pending Portout', 'Ports', 'View pending port-outs for this carrier.', true),
  p(924, 'Plintron Completed Portout', 'Ports', 'View completed port-outs for this carrier.', true),
  p(937, 'Plum Pending Portout', 'Ports', 'View pending port-outs for this carrier.', true),
  p(936, 'Plum Completed Portout', 'Ports', 'View completed port-outs for this carrier.', true),

  // Orders
  p(109, 'Prepaid/postpaid orders', 'Orders', 'Open the orders menu.', true),
  p(
    1164,
    'Lifeline Enrollment New Orders',
    'Orders',
    "Start and continue Lifeline applications, and see the last 4 digits of an applicant's SSN.",
    true,
  ),
  p(
    1165,
    'Lifeline Enrollment All Orders',
    'Orders',
    'Open all Lifeline applications, Search account and Check status.',
    true,
  ),
  p(
    1166,
    'Lifeline Enrollment Completed Orders',
    'Orders',
    'Open the completed Lifeline applications.',
    true,
  ),
  p(
    1167,
    'Lifeline Enrollment Incomplete Orders',
    'Orders',
    'Open the incomplete Lifeline applications.',
    true,
  ),
  p(
    1168,
    'Lifeline Enrollment Rejected Orders',
    'Orders',
    'Open the Lifeline applications the National Verifier rejected.',
    true,
  ),
  p(1142, 'New enrollments', 'Orders', 'Start a new enrollment.', true),
  p(1170, 'All enrollments', 'Orders', 'List all enrollments.', true),
  p(731, 'Bulk enrollment', 'Orders', 'Upload enrollments in bulk.', true),
  p(794, 'Bulk Enrollment Report', 'Orders', 'View bulk enrollment batch results.', true),
  p(816, 'Carrier service availability', 'Orders', 'Check carrier coverage for a ZIP code.', true),
  p(1171, 'Completed enrollments', 'Orders', 'List completed enrollments.', true),
  p(
    1172,
    'Incomplete enrollments',
    'Orders',
    'List incomplete enrollments and send reminders.',
    true,
  ),

  // Review
  p(426, 'Review', 'Review', 'Open the Review menu.', true),
  p(984, 'Fraud Orders Review Queue', 'Review', 'Audit orders flagged for fraud review.', true),

  // Inventory
  p(382, 'Inventory', 'Inventory', 'Open the Inventory menu.', true),
  p(
    345,
    'Manage Inventory',
    'Inventory',
    'View the inventory dashboard and add or update units.',
    true,
  ),
  p(7, 'Dropship Orders', 'Inventory', 'View pending dropship orders.', true),
  p(1066, 'Shipping Queue', 'Inventory', 'Fulfil orders in the shipping queue.', true),
  p(664, 'Re-process failed order', 'Inventory', 'Retry orders that failed provisioning.'),
  p(314, 'Manage Model', 'Inventory', 'Maintain the device and product catalogue.', true),
  p(679, 'Manage Equipment', 'Inventory', 'Maintain equipment records.', true),
  p(683, 'Manage Equipment Type', 'Inventory', 'Maintain equipment types.', true),
  p(727, 'Manage Equipment Status Name', 'Inventory', 'Maintain equipment status names.', true),
  p(
    732,
    'Manage Change Reason',
    'Inventory',
    'Maintain reasons used when a SIM or device changes.',
    true,
  ),
  p(350, 'Inventory Report', 'Inventory', 'Open SIM and device inventory reports.', true),
  p(19, 'Inventory Reporting', 'Inventory', 'Open the inventory reporting hub.', true),
  p(182, 'SIM Summary', 'Inventory', 'Count the SIMs in stock, free and used.', true),
  p(
    270,
    'Check SIM Compatibility',
    'Inventory',
    'Ask the carrier whether a device can be used on its network.',
    true,
  ),
  p(214, 'GSM Device Inventory for Agents', 'Inventory', 'Count devices per agent and box.', true),
  p(215, 'SIM Inventory for Agents', 'Inventory', 'Count SIMs per agent and box.', true),
  p(
    306,
    'Manage Unassigned Inventory',
    'Inventory',
    'SIMs of each master agent and how far down they went.',
    true,
  ),
  p(
    309,
    'Manage Unassigned Device',
    'Inventory',
    'Devices of each master agent and how far down they went.',
    true,
  ),
  p(
    644,
    'Unassigned Stock Report',
    'Inventory',
    'SIMs of each upload that are still available.',
    true,
  ),
  p(
    187,
    'ESN SIM Batch Report',
    'Inventory',
    'Uploads of SIMs (Add Bulk Unit) and how each went.',
    true,
  ),
  p(
    185,
    'Device Report',
    'Inventory',
    'Uploads of devices (Add Bulk Unit) and how each went.',
    true,
  ),
  p(
    508,
    'Universal Inventory Report',
    'Inventory',
    'Every SIM and device of the hierarchy, by agent, box, model, type, status and carrier.',
    true,
  ),
  p(183, 'Vendor Summary Report', 'Inventory', 'Units of each model counted by status.', true),
  p(
    477,
    'Ship ESN SIM to Agent Store By Creating Boxes',
    'Inventory',
    'The boxes of SIMs (units sharing a Box ID) and their size.',
    true,
  ),
  p(
    478,
    'Ship Device to Agent Store By Creating Boxes',
    'Inventory',
    'The boxes of devices (units sharing a Box ID) and their size.',
    true,
  ),
  p(656, 'UICCID Swap Report', 'Inventory', 'The ESN/SIM changes of customers.', true),
  p(
    692,
    'Refund from Sale Screen',
    'Billing',
    'Refund a paid invoice, in full or in part, with a reason (Billing and Invoice).',
    true,
  ),
  p(655, 'Payment Refunds Report', 'Reports', 'The refunds made on invoices.', true),
  p(319, 'Credit Card Payment Handover', 'Billing', 'Take the enrollment payment by card.', true),
  p(404, 'Upgrade Topup CC', 'Billing', 'Pay a plan renewal or plan change by card.', true),
  p(414, 'Add Minutes CC', 'Billing', 'Pay a top-up or a line restore by card.', true),
  p(649, 'Pay Bill Credit Card', 'Billing', 'Pay invoices by card.', true),
  p(568, 'My Wallet Credit Card', 'Billing', "Add money to a customer's wallet by card.", true),
  p(
    777,
    'Save card Details',
    'Billing',
    'Keep a card typed in as a saved card of the customer.',
    true,
  ),

  // Bulk Process
  p(347, 'Bulk Process', 'Bulk Process', 'Open the bulk process submenu.', true),
  p(351, 'Bulk Clear SIM', 'Bulk Process', 'Release SIMs back to free stock in bulk.', true),
  p(884, 'Bulk Print Label', 'Bulk Process', 'Print shipping labels in bulk.', true),
  p(352, 'Bulk Reassign SIM', 'Bulk Process', 'Move SIMs to another agent in bulk.', true),
  p(310, 'Re-assign SIM Report', 'Bulk Process', 'View SIM reassignment batch results.', true),
  p(353, 'Bulk Reassign Device', 'Bulk Process', 'Move devices to another agent in bulk.', true),
  p(
    311,
    'Re-assign Device Report',
    'Bulk Process',
    'View device reassignment batch results.',
    true,
  ),
  p(
    552,
    'Bulk SIM Reassign by Agent',
    'Bulk Process',
    'Move SIMs within your own hierarchy.',
    true,
  ),
  p(
    551,
    'Bulk Device Reassign by Agent',
    'Bulk Process',
    'Move devices within your own hierarchy.',
    true,
  ),
  p(
    868,
    'Bulk Clear Customer Device',
    'Bulk Process',
    'Detach devices from customers in bulk.',
    true,
  ),
  p(354, 'Bulk Clear Device', 'Bulk Process', 'Release devices back to free stock in bulk.', true),
  p(180, 'Clear Device Report', 'Bulk Process', 'View device clearing batch results.', true),
  p(355, 'Bulk DeProvision SIM', 'Bulk Process', 'De-provision unused SIMs at the carrier.', true),
  p(356, 'Bulk Deactivate MDN', 'Bulk Process', 'Disconnect lines in bulk.', true),
  p(358, 'Bulk Swap SIM', 'Bulk Process', 'Swap SIMs in bulk.', true),
  p(357, 'Bulk Swap MDN', 'Bulk Process', 'Change phone numbers in bulk.', true),
  p(
    804,
    'Bulk Swap MDN by Customer ID',
    'Bulk Process',
    'Change phone numbers in bulk by customer.',
    true,
  ),
  p(830, 'Bulk MDN Topup', 'Bulk Process', 'Apply top-ups in bulk.', true),
  p(360, 'Bulk Customer Plan Change', 'Bulk Process', 'Change plans in bulk.', true),
  p(361, 'Bulk Line Status', 'Bulk Process', 'Check carrier line status in bulk.', true),
  p(362, 'Bulk Hotline', 'Bulk Process', 'Hotline lines in bulk.', true),
  p(363, 'Bulk Unhotline', 'Bulk Process', 'Remove hotline in bulk.', true),
  p(
    364,
    'Bulk Device Status and Reassign',
    'Bulk Process',
    'Set device status and owner in bulk.',
    true,
  ),
  p(
    1058,
    'Bulk SIM Device Information',
    'Bulk Process',
    'Look up SIM and device information in bulk.',
    true,
  ),
  p(365, 'Bulk SIM Status and Reassign', 'Bulk Process', 'Set SIM status and owner in bulk.', true),
  p(856, 'Bulk Customer Update', 'Bulk Process', 'Update customer records from CSV.', true),
  p(857, 'Bulk NLAD Process', 'Bulk Process', 'Update NLAD records from CSV.', true),

  // Shipment
  p(383, 'Shipment', 'Shipment', 'Open the Shipment menu.', true),
  p(223, 'Shipment Dashboard', 'Shipment', 'Scan, search and track shipments.', true),
  p(
    538,
    'Equipment & Accessories Request',
    'Shipment',
    'Work equipment and accessory requests.',
    true,
  ),

  // Reports
  p(224, 'Reports', 'Reports', 'Open the Reports menu.', true),
  p(428, 'All Reports', 'Reports', 'Open the standard report catalogue.', true),
  p(429, 'Customize Reports', 'Reports', 'Open the customisable report catalogue.', true),
  p(520, 'Saved report settings', 'Reports', 'Manage saved report parameters.', true),
  p(527, 'Scheduled reports', 'Reports', 'Manage scheduled reports.', true),

  // APIs
  p(431, 'APIs', 'APIs', 'Open the APIs menu.', true),
  p(806, 'REST API docs', 'APIs', 'Open the API documentation.', true),
  p(808, 'REST API Log', 'APIs', 'View the REST API request log.', true),
  p(1101, 'USPS API Log', 'APIs', 'View the address-validation request log.', true),

  // Notifications
  p(677, 'Notifications', 'Notifications', 'Open the Notifications menu.', true),
  p(668, 'Add email notification', 'Notifications', 'Create an email notification template.', true),
  p(669, 'View email notifications', 'Notifications', 'List and edit email templates.', true),
  p(675, 'Add SMS notification', 'Notifications', 'Create an SMS notification template.', true),
  p(676, 'View SMS notifications', 'Notifications', 'List and edit SMS templates.', true),
  p(832, 'Add IVR notification', 'Notifications', 'Create an IVR notification.', true),
  p(833, 'View IVR notifications', 'Notifications', 'List and edit IVR notifications.', true),

  // Campaigns
  p(760, 'Campaigns', 'Campaigns', 'Open the Campaigns menu.', true),
  p(761, 'Create Campaign', 'Campaigns', 'Create an SMS or email campaign.', true),
  p(762, 'View Campaign List', 'Campaigns', 'List and edit campaigns.', true),
  p(
    763,
    'Attach customers to a campaign',
    'Campaigns',
    'Upload the audience for a campaign.',
    true,
  ),

  // Other modules
  p(722, 'Tickets', 'Support', 'Work the ticket queue.', true),
  p(852, 'Coupon codes', 'Promotions', 'Manage coupon codes.', true),
  p(1141, 'Digital Card', 'Promotions', 'Generate and assign digital cards.', true),

  // Settings
  p(232, 'Plan Config', 'Settings', 'Maintain plans, top-ups and carrier codes.', true),
  p(480, 'User Settings', 'Settings', 'Open user settings.', true),
  p(234, 'System Config', 'Settings', 'Change system configuration switches.', true),
  p(
    236,
    'Shipper Settings',
    'Settings',
    'Maintain shipper credentials and ship-from addresses.',
    true,
  ),
  p(237, 'Service Area', 'Settings', 'Maintain serviceable ZIP codes.', true),
  p(238, 'Unlock User', 'Settings', 'Unlock a locked user account.', true),
  p(239, 'Payment Gateway', 'Settings', 'Maintain payment gateway settings.', true),
  p(1203, 'Tax Config', 'Settings', 'Maintain tax rules.', true),
  p(
    240,
    'Other Settings',
    'Settings',
    'Open reasons, note types, blacklists and similar lists.',
    true,
  ),
  p(448, 'Disconnection Reason', 'Settings', 'Maintain disconnection reasons.', true),
  p(996, 'Suspend Reason', 'Settings', 'Maintain suspend reasons.', true),
  p(450, 'Manage Note Type', 'Settings', 'Maintain customer note types.', true),

  // User Settings
  p(12, 'Manage user groups', 'User Settings', 'Create groups and edit their permissions.', true),
  p(120, 'Manage master agent', 'User Settings', 'Create and maintain master agents.', true),
  p(121, 'Manage distributor', 'User Settings', 'Create and maintain distributors.', true),
  p(122, 'Manage retailer', 'User Settings', 'Create and maintain retailers.', true),
  p(123, 'Manage employee', 'User Settings', 'Create and maintain employees.', true),
  p(792, 'Commission matrix', 'User Settings', 'Maintain commission matrices.', true),
  p(871, 'Search user', 'User Settings', 'Search users across all levels.', true),
  p(61, 'Activation Report', 'Reports', 'Run the Activation Report.', true),
  p(125, 'Customer Line Status', 'Reports', 'Run the Customer Line Status report.', true),
  p(126, 'Customer List', 'Reports', 'Run the Customer List report.', true),
  p(695, 'Enhance Payment Report', 'Reports', 'Run the Enhance Payment Report.', true),
  p(742, 'OSS API Log', 'Reports', 'Run the OSS API Log report.', true),
  p(553, 'Payment Report By Store', 'Reports', 'Run the Payment Report By Store.', true),
  p(1186, 'Revenue Report', 'Reports', 'Run the Revenue Report.', true),
  p(159, 'User Login Log Report', 'Reports', 'Run the User Login Log Report.', true),
  p(962, 'Account Activations Report', 'Reports', 'Run the Account Activations Report.', true),
  p(136, 'Customer Order', 'Reports', 'Run the Customer Order report.', true),
  p(
    139,
    'Customers Reactivation Report',
    'Reports',
    'Run the Customers Reactivation Report.',
    true,
  ),
  p(
    563,
    'Disconnection Report By Store',
    'Reports',
    'Run the Disconnection Report By Store.',
    true,
  ),
  p(
    144,
    'Non Life-line Report (New Activation)',
    'Reports',
    'Run the Non Life-line new activation report.',
    true,
  ),
  p(1185, 'Order Status Summary', 'Reports', 'Run the Order Status Summary.', true),
  p(951, 'Payment Detail Report', 'Reports', 'Run the Payment Detail Report.', true),
  p(957, 'Plan List Report', 'Reports', 'Run the Plan List Report.', true),
  p(
    793,
    'Prepaid plan payment status',
    'Reports',
    'Run the Prepaid plan payment status report.',
    true,
  ),
  p(935, 'Wallet Balance Report', 'Reports', 'Run the Wallet Balance Report.', true),
  p(1036, 'Agent Sales Report', 'Reports', 'Run the Agent Sales Report.', true),
  p(69, 'Equipment Purchase', 'Customer Service', 'Sell or resend equipment to a customer.', true),
  p(
    409,
    'Equipment Purchase - Cash',
    'Customer Service',
    'Take cash for an equipment purchase.',
    true,
  ),
  p(
    572,
    'Free Equipment Purchase',
    'Customer Service',
    'Give equipment to a customer without payment.',
    true,
  ),
  p(
    252,
    'Handover - Shipment',
    'Orders',
    'Use the handover type Shipment at enrollment step 3.',
    true,
  ),
  p(456, 'Add Wallet', 'Customer Service', "Add cash to a customer's wallet.", true),
  p(765, 'Adjust Wallet', 'Customer Service', "Correct a customer's wallet balance.", true),
  p(569, 'Dealer Wallet - Cash', 'Customer Service', 'Add cash to the dealer wallet.', true),
  p(
    869,
    'Clear Customer Device Report',
    'Bulk Process',
    'View customer device clearing batch results.',
    true,
  ),
  p(22, 'De-enroll SIM Report', 'Bulk Process', 'View SIM de-provisioning batch results.', true),
  p(23, 'Deactivate MDN Report', 'Bulk Process', 'View bulk disconnection batch results.', true),
  p(24, 'Swap SIM Batch Report', 'Bulk Process', 'View bulk SIM swap batch results.', true),
  p(540, 'Bulk Swap MDN Report', 'Bulk Process', 'View bulk number change batch results.', true),
  p(831, 'MDN Topup Report', 'Bulk Process', 'View bulk top-up batch results.', true),
  p(
    496,
    'Bulk Customer Plan Change Report',
    'Bulk Process',
    'View bulk plan change batch results.',
    true,
  ),
  p(436, 'Bulk Hotline Report', 'Bulk Process', 'View bulk hotline batch results.', true),
  p(437, 'Bulk Unhotline Report', 'Bulk Process', 'View bulk unhotline batch results.', true),
  p(843, 'Manage Discount Credit Types', 'Other Settings', 'Manage discount credit types.', true),
  p(1044, 'Manage Sub Note Type', 'Other Settings', 'Manage sub note types.', true),
  p(
    721,
    'Payment Processing Fee Config',
    'Other Settings',
    'Manage payment processing fees.',
    true,
  ),
  p(1076, 'Manage Promo Discounts', 'Other Settings', 'Manage promo discounts.', true),
  p(
    449,
    'Note Type (Bulk Upload Notes)',
    'Other Settings',
    'Upload notes for many customers from a file.',
    true,
  ),
  p(451, 'Note Type (Report Note Type)', 'Other Settings', 'Report of customer notes.', true),
  p(90, 'Change Mobile number', 'Customer Service', 'Give a line a new telephone number.', true),
  p(101, 'Change Device ID', 'Customer Service', 'Give a customer another device.', true),
  p(
    60,
    'Add Topup - Minute/Text',
    'Customer Service',
    'Add a minute or text top-up to a line.',
    true,
  ),
  p(64, 'Add Data Topup', 'Customer Service', 'Add a data top-up to a line.', true),
  p(77, 'Plan Change', 'Customer Service', 'Change the plan of a line.', true),
  p(
    1250,
    'Matrix commission: Master Agent',
    'User Settings',
    'Give a master agent a commission matrix.',
    true,
  ),
  p(
    1247,
    'Matrix commission: Distributor',
    'User Settings',
    'Give a distributor a commission matrix.',
    true,
  ),
  p(
    1248,
    'Matrix commission: Retailer',
    'User Settings',
    'Give a retailer a commission matrix.',
    true,
  ),
  p(
    1249,
    'Matrix commission: Employee',
    'User Settings',
    'Give an employee a commission matrix.',
    true,
  ),
  p(
    253,
    'Customer SSN and DOB',
    'Customer Service',
    "See and change a customer's date of birth and the last four digits of the SSN.",
    true,
  ),
  p(759, 'Customer Tags', 'Customer Service', 'Add and remove tags on a customer.', true),
  p(519, 'Plan Renew', 'Customer Service', 'Renew the plan of a line and take the payment.', true),
  p(
    1195,
    'Apply Coupon',
    'Customer Service',
    "Use a coupon on a customer's charge and see the coupons log.",
    true,
  ),
  p(
    410,
    'Money Order',
    'Customer Service',
    'Take a money order for equipment, a plan renewal or a plan change.',
    true,
  ),
  p(
    411,
    'Money Gram',
    'Customer Service',
    'Take a MoneyGram payment for equipment, a plan renewal or a plan change.',
    true,
  ),
  p(
    412,
    'Wallet',
    'Customer Service',
    'Pay for equipment, a plan renewal or a plan change from the customer wallet.',
    true,
  ),
  p(197, 'Agent-wise Topup Report', 'Reports', 'Top-ups sold, by agent.', true),
  p(693, 'Invoice Report', 'Reports', 'Invoices of every kind.', true),
  p(670, 'Prepaid Renewed Report', 'Reports', 'Plan renewals taken.', true),
  p(671, 'Prepaid Next Renew Report', 'Reports', 'Plans that come up for renewal.', true),
  p(131, 'Sales Channel Report', 'Reports', 'Enrollments by sales channel.', true),
  p(1200, 'User Activity Report', 'Reports', 'What users did, from the audit log.', true),
  p(317, 'Paid - Equipment', 'Reports', 'Equipment that was paid for.', true),
  p(619, 'Wallet Details Report', 'Reports', 'Every movement of customer wallets.', true),
  p(929, 'Order Shipment Report', 'Reports', 'Shipment orders with their tracking.', true),
  p(145, 'Customer Note', 'Reports', 'Notes written on customers.', true),
  p(157, 'Customer Disconnections with Reason', 'Reports', 'Disconnected customers and why.', true),
  p(40, 'Active Customer With Plan', 'Reports', 'Active customers and the plan they are on.', true),
  p(503, 'Swap Device Report', 'Reports', 'SIM and device changes.', true),
  p(156, 'Top Ups by State', 'Reports', 'Top-ups counted by state.', true),
  p(151, 'Enrollment Activity Report', 'Reports', 'Enrollments and where each stands.', true),
  p(
    152,
    'Handover Activity Report',
    'Reports',
    'Enrollments that reached the handover step.',
    true,
  ),
  p(574, 'Auto Pay', 'Customer Service', 'Switch Auto Pay of a line on or off.', true),
  p(798, 'All Commission Matrix', 'Reports', 'Commission earned by agents.', true),
  p(
    646,
    'Change UICCID',
    'Customer Service',
    'Move a line to another SIM card and keep its number.',
    true,
  ),
  p(1078, 'E-Sim Change', 'Customer Service', 'Move a line to an eSIM from stock.', true),
  p(530, 'Change Carrier', 'Customer Service', 'Move a line to another carrier.', true),
  p(
    346,
    'Edit Device Info',
    'Customer Service',
    'Record the device a customer brought themselves.',
    true,
  ),
  p(
    92,
    'Features and Services',
    'Customer Service',
    'Load the features of a line and switch Wifi Calling.',
    true,
  ),
  p(254, 'Usage', 'Customer Service', 'See the usage the carrier reports for a line.', true),
  p(
    99,
    'Skip Payment',
    'Customer Service',
    'Record a sale without taking money; the invoice is marked paid with SKIP.',
    true,
  ),
  p(518, 'Plan Change Mode', 'Customer Service', 'Choose when a plan change takes effect.', true),
  p(628, 'Plan Purchase: Instant', 'Customer Service', 'Renew or change a plan at once.', true),
  p(
    629,
    'Plan Purchase: On Expiry',
    'Customer Service',
    'Renew or change a plan on its expiry date, paid now.',
    true,
  ),
  p(
    630,
    'Plan Purchase: Custom Date',
    'Customer Service',
    'Renew or change a plan on a chosen date, paid now.',
    true,
  ),
  p(
    631,
    'Keep the Expiry as Original Date',
    'Customer Service',
    'Keep the plan expiry cycle when renewing or changing a plan.',
    true,
  ),
  p(72, 'Regular Invoices', 'Customer Service', 'View the invoices of a customer.', true),
  p(
    67,
    'Pay Invoice(s)',
    'Customer Service',
    'Pay the unpaid invoices of a customer; more than is due goes to the wallet.',
    true,
  ),
  p(
    454,
    'Add Ad hoc Invoice',
    'Customer Service',
    'Add a charge outside any sale; it starts unpaid.',
    true,
  ),
  p(463, 'Payments', 'Customer Service', 'View the payments of a customer.', true),
  p(466, 'Wallet Log', 'Customer Service', 'View the wallet log of a customer.', true),
  p(
    460,
    'E-Bill Setting',
    'Customer Service',
    'Choose how the bill is sent: Email, SMS or both.',
    true,
  ),
  p(
    609,
    'Void Invoice/Payment',
    'Customer Service',
    'Void an invoice on which nothing has been paid.',
    true,
  ),
  p(650, 'Pay Invoice: Cash', 'Customer Service', 'Pay invoices in cash.', true),
  p(
    652,
    'Pay Invoice: Wallet Balance',
    'Customer Service',
    'Pay invoices from the customer wallet.',
    true,
  ),
  p(825, 'Money Gram', 'Customer Service', 'Pay invoices by MoneyGram.', true),
  p(823, 'Cheque', 'Customer Service', 'Pay invoices by cheque.', true),
  p(
    1243,
    'Add/Update matrix commission',
    'User Settings',
    'Add a commission matrix, add a state to it and change its amounts.',
    true,
  ),
  p(415, 'Top-up: Cash', 'Customer Service', 'Pay a minute/text top-up in cash.', true),
  p(
    416,
    'Top-up: Money Order',
    'Customer Service',
    'Pay a minute/text top-up by money order.',
    true,
  ),
  p(417, 'Top-up: Money Gram', 'Customer Service', 'Pay a minute/text top-up by MoneyGram.', true),
  p(
    418,
    'Top-up: Wallet',
    'Customer Service',
    "Pay a minute/text top-up from the customer's wallet.",
    true,
  ),
  p(419, 'Free Topup', 'Customer Service', 'Add a top-up without payment.', true),
  p(421, 'Data top-up: Cash', 'Customer Service', 'Pay a data top-up in cash.', true),
  p(422, 'Data top-up: Money Order', 'Customer Service', 'Pay a data top-up by money order.', true),
  p(423, 'Data top-up: Money Gram', 'Customer Service', 'Pay a data top-up by MoneyGram.', true),
  p(
    424,
    'Data top-up: Wallet',
    'Customer Service',
    "Pay a data top-up from the customer's wallet.",
    true,
  ),
  p(191, 'Activity Report', 'Reports', 'Enrollments per user who took them.', true),
  p(789, 'Agent Order Report', 'Reports', 'Employees and when each last made a customer.', true),
  p(
    1140,
    'Customer Billing Code/PWG Cost Plan Report',
    'Reports',
    "The carrier code each customer's plan is sold under.",
    true,
  ),
  p(665, 'Autopay Report', 'Reports', 'Customers with Auto Pay on or cancelled.', true),
  p(
    666,
    'Autopay Transaction Report',
    'Reports',
    'What Auto Pay paid, and the attempts it could not pay.',
    true,
  ),
  p(1, 'Add Units', 'Inventory', 'Add SIM and device units from the Manage Inventory tiles.', true),
  p(
    21,
    'Inventory Dashboard',
    'Inventory',
    'Open the Inventory Dashboard from the Manage Inventory tiles.',
    true,
  ),
  p(
    50,
    'Change ESN/SIM Status',
    'Inventory',
    'Change the status and agent of SIMs (Change SIM Status & Agent Name).',
    true,
  ),
  p(
    169,
    'Equipment & Accessories Request',
    'Inventory',
    'Open equipment and accessories requests from the Manage Inventory tiles.',
    true,
  ),
  p(
    391,
    'Update Inventory',
    'Inventory',
    'Update the details of SIMs and devices, one at a time or from a file.',
    true,
  ),
  p(47, 'Adjustments', 'Inventory', 'Open the single re-assign pages.', true),
  p(
    38,
    'Single Inventory Re-assign',
    'Inventory',
    'Find SIMs by number or box number and move them to an agent.',
    true,
  ),
  p(
    32,
    'Single Device Re-assign',
    'Inventory',
    'Find devices by number or box number and move them to an agent.',
    true,
  ),
  p(
    188,
    'Inventory Update Batch Report',
    'Inventory',
    'Read the batches of the ESN/SIM update upload.',
    true,
  ),
  p(
    476,
    'Inventory Update Device Batch Report',
    'Inventory',
    'Read the batches of the device update upload.',
    true,
  ),
  p(
    307,
    'Agent Inventory Report',
    'Inventory',
    'Open the SIMs of agents with their customers.',
    true,
  ),
  p(
    308,
    'Agent Device Report',
    'Inventory',
    'Open the devices of agents with their customers.',
    true,
  ),
  p(
    486,
    'Transaction Inventory Report ESN SIM',
    'Inventory',
    "Open Inventory Report's Transaction Inventory Report (SIM).",
    true,
  ),
  p(
    487,
    'Agent wise Inventory Report ESN SIM',
    'Inventory',
    "Open Inventory Report's Agent Wise Inventory Report (SIM).",
    true,
  ),
  p(
    488,
    'Inventory Snapshot ESN SIM',
    'Inventory',
    "Open Inventory Report's Inventory Snapshot (SIM).",
    true,
  ),
  p(
    489,
    'ESN SIM Inventory Report',
    'Inventory',
    "Open Inventory Report's SIM Inventory Detail.",
    true,
  ),
  p(
    490,
    'Transaction IMEI Inventory Report',
    'Inventory',
    "Open Inventory Report's Transaction Device Inventory Report.",
    true,
  ),
  p(
    491,
    'Agent wise IMEI Inventory Report',
    'Inventory',
    "Open Inventory Report's Agent Device Inventory.",
    true,
  ),
  p(
    492,
    'IMEI Inventory Snapshot',
    'Inventory',
    "Open Inventory Report's Inventory Device Snapshot.",
    true,
  ),
  p(
    493,
    'GSM Device Inventory Report',
    'Inventory',
    "Open Inventory Report's Device Inventory Detail.",
    true,
  ),
  p(501, 'GSM Devices By Agent', 'Inventory', "Open Inventory Report's Device By Agent.", true),
  p(
    502,
    'ESN SIM Inventory By Agent',
    'Inventory',
    "Open Inventory Report's SIM Inventory By Agent.",
    true,
  ),
  p(
    965,
    'Allow Use to access System Health Status',
    'Dashboard',
    "Open the header's System Health Status (Monitoring Panel) without being an administrator.",
    true,
  ),
  ...REFERENCE_ONLY_PERMISSIONS.map(([code, name, category]): PermissionDef => ({
    code: `PC${code}`,
    name,
    category,
    description: 'Not used by 212Ez yet: listed so groups match the reference.',
    implemented: false,
    referenceOnly: true,
  })),
];

export const PERMISSION_CODES = PERMISSIONS.map((x) => x.code);
const BY_CODE = new Map(PERMISSIONS.map((x) => [x.code, x]));
export const isKnownPermission = (code: string): boolean => BY_CODE.has(code);
export const getPermission = (code: string): PermissionDef | undefined => BY_CODE.get(code);

/** Permission that guards the management screen for each hierarchy level. */
export const LEVEL_PERMISSION = {
  admin: 'PC123',
  master: 'PC120',
  distributor: 'PC121',
  retailer: 'PC122',
  employee: 'PC123',
} as const;
